// Conexiones y alta de cuentas de Meta (Entrega F). Solo ADMIN, salvo el estado público de una solicitud
// de eliminación. Ningún token pasa por el navegador: el alta vuelve de Meta al backend.
import type {
  AdminConnectionView, ConnectPageResultView, DataDeletionRequestView, DataDeletionStatus, DataDeletionStatusView,
  MetaOnboardingStartView, MetaOnboardingView,
} from '@hilo/contracts';
import { api } from './api';

/** Primer elemento de las claves de consultas públicas, que no dependen de la sesión. */
export const PUBLIC_QUERY = 'public';

export const adminKeys = {
  connections: ['admin', 'connections'] as const,
  deletionRequests: ['admin', 'deletion-requests'] as const,
  onboarding: (id: string) => ['admin', 'onboarding', id] as const,
  /** Pública: la conserva forgetSession (un visitante sin sesión recibe 401 en /auth/session). */
  deletionStatus: (code: string) => [PUBLIC_QUERY, 'deletion-status', code] as const,
};

export const fetchAdminConnections = () => api<AdminConnectionView[]>('GET', '/connections/admin');
export const setConnectionAccess = (id: string, userIds: string[]) => api<void>('PUT', `/connections/${id}/access`, { body: { userIds } });
export const revokeCredential = (id: string) => api<void>('DELETE', `/connections/${id}/credential`);
export const setConnectionMode = (id: string, mode: AdminConnectionView['mode']) => api<void>('PUT', `/connections/${id}/mode`, { body: { mode } });

export const startOnboarding = () => api<MetaOnboardingStartView>('POST', '/onboarding/meta/start');
export const startInstagramOnboarding = () => api<MetaOnboardingStartView>('POST', '/onboarding/instagram/start');
export const fetchOnboarding = (id: string) => api<MetaOnboardingView>('GET', `/onboarding/meta/${id}`);
export const connectPage = (id: string, pageId: string) => api<ConnectPageResultView>('POST', `/onboarding/meta/${id}/connect`, { body: { pageId } });

export const fetchDeletionRequests = () => api<DataDeletionRequestView[]>('GET', '/data-deletion-requests');
export const updateDeletionRequest = (id: string, body: { responsibleId?: string | null; status?: DataDeletionStatus; publicNote?: string | null }) =>
  api<DataDeletionRequestView>('PATCH', `/data-deletion-requests/${id}`, { body });
export const fetchDeletionStatus = (code: string) => api<DataDeletionStatusView>('GET', `/data-deletion/${encodeURIComponent(code)}`);

export const DELETION_STATUS_LABEL: Record<DataDeletionStatus, string> = {
  IN_PROGRESS: 'En curso',
  COMPLETED: 'Completada',
  RETAINED: 'Datos conservados',
};

/** Códigos propios con los que el retorno de Meta vuelve a Conexiones (`?alta=error&codigo=…`). */
const ONBOARDING_FAILURE: Record<string, string> = {
  INSTAGRAM_ALREADY_CONNECTED: 'Esta cuenta de Instagram ya está conectada. Vuelve a Conexiones.',
  INSTAGRAM_PERMISSIONS_MISSING: 'Faltan permisos de Instagram. Vuelve a autorizar perfil básico y mensajes.',
  CONNECTION_OTHER_SETUP: 'Esta cuenta está configurada de otra forma. Revisa su conexión antes de cambiar la modalidad.',
  UNAVAILABLE: 'El alta con Meta no está disponible en este entorno.',
  STATE_INVALID: 'La autorización no es válida o caducó. Vuelve a empezar.',
  SESSION_REQUIRED: 'Tu sesión cambió durante la autorización. Vuelve a empezar desde esta pantalla.',
  CANCELLED: 'Cancelaste la autorización en Meta. No se conectó nada.',
  CODE_INVALID: 'Meta no aceptó la autorización. Vuelve a empezar.',
  TOKEN_INVALID: 'Meta devolvió una autorización que no es de la app Hilo o que ya caducó. No se conectó nada.',
  PERMISSIONS_MISSING: 'Faltan permisos de Messenger. Vuelve a empezar y acepta todos los permisos que pide Hilo.',
  NO_PAGES: 'No elegiste ninguna página en Meta. Vuelve a empezar y marca la página que quieres conectar.',
  TOO_MANY_PAGES: 'Autorizaste demasiadas páginas. Vuelve a empezar y elige solo las que Hilo va a atender.',
  GRAPH_UNAVAILABLE: 'Meta no respondió. Inténtalo de nuevo en unos minutos.',
  GRAPH_REJECTED: 'Meta rechazó una consulta del alta. No se conectó nada.',
  GRAPH_INVALID_RESPONSE: 'Meta respondió algo inesperado. No se conectó nada.',
  EXPIRED: 'Pasó el tiempo para elegir la página. Vuelve a empezar.',
  DEAUTHORIZED: 'La app Hilo se quitó en Facebook mientras tanto. Vuelve a empezar si quieres conectarla.',
  DATA_DELETION: 'Se pidió eliminar los datos de esta autorización. Vuelve a empezar si quieres conectarla.',
  META_REVOKED: 'Meta avisó de que se retiró el permiso de la app Hilo o se pidió borrar sus datos mientras autorizabas. No se guardó nada; vuelve a empezar si quieres conectarla.',
  TOKEN_UNREADABLE: 'Hilo ya no puede leer esta autorización (cambió la clave del servidor). Vuelve a empezar.',
  INTERNAL: 'Algo falló en Hilo. No se conectó nada.',
};

export function onboardingFailureText(code: string | null): string {
  if (!code) return 'No se pudo completar el alta.';
  return ONBOARDING_FAILURE[code] ?? `No se pudo completar el alta (código ${code}).`;
}

/** Errores de las acciones de Conexiones que tienen un texto propio. */
const ACTION_ERROR: Record<string, string> = {
  INSTAGRAM_UNSUBSCRIBE_FAILED: 'Meta no confirmó la retirada de la suscripción. Conservamos la credencial para que puedas reintentarlo.',
  PROVIDER_DISABLED: 'Las llamadas a Meta están apagadas en este entorno. Se encienden solo en una ventana autorizada.',
  ONBOARDING_NOT_CONFIGURED: 'El alta con Meta no está configurada en este entorno (falta la app Hilo en el servidor).',
  CONNECTION_OTHER_SETUP: 'Esta página o su Instagram ya están conectados de otra forma. Hilo no los modifica: revísalo con quien configuró el servidor.',
  PAGE_ALREADY_CONNECTED: 'Esta página ya está conectada. Elige otra página o vuelve a Conexiones.',
  PAGE_NOT_AUTHORIZED: 'Esa página no está entre las que autorizaste en Meta.',
  PAGE_TASKS_MISSING: 'No tienes sobre esa página las tareas que necesita Hilo (mensajes y administración).',
  ONBOARDING_BUSY: 'Esta autorización se está usando en otra pestaña. Espera un momento.',
  ONBOARDING_NOT_READY: 'Esta autorización ya no está disponible. Vuelve a empezar desde Conexiones.',
  SUBSCRIPTION_FAILED: 'Meta no confirmó la suscripción de la página. No se conectó nada; inténtalo de nuevo.',
  GRAPH_UNAVAILABLE: 'Meta no respondió. No se conectó nada; inténtalo de nuevo.',
  CREDENTIAL_NOT_STORED: 'Esta conexión se configura en el servidor; no se revoca desde aquí.',
  INVALID_RESPONSIBLE: 'El responsable debe ser una persona con rol de administración y activa.',
  NOTE_REQUIRED: 'Para conservar datos hay que explicar públicamente qué se conserva y por qué.',
  INVALID_USER: 'Alguna de las personas elegidas ya no existe o está desactivada.',
  EMAIL_TAKEN: 'Ya hay una persona con ese correo en Hilo.',
  LAST_ADMIN: 'Debe quedar al menos una persona de administración activa.',
  WHATSAPP_PRODUCTION_NOT_READY: 'WhatsApp pasa a producción con el número de la empresa, cuando el envío por BSUID esté resuelto.',
  CONNECTION_DISABLED: 'Esta conexión está desactivada. Conéctala otra vez antes de pasarla a producción.',
  REVIEWER_HAS_ACCESS: 'El operador revisor de Meta tiene acceso a esta cuenta. Quítaselo en Accesos antes de pasarla a producción.',
  REVIEWER_PRODUCTION_ACCESS: 'El operador revisor de Meta solo puede atender cuentas que siempre han estado en pruebas, nunca clientes reales.',
  REVIEWER_ROLE_LOCKED: 'El operador revisor de Meta solo puede tener el rol de agente.',
};

export function adminActionError(code: string): string | undefined {
  return ACTION_ERROR[code];
}
